/**
 * The Timeline's commit overlay for one timeline (roadmap D180): where each
 * commit goes, the lanes it adds, each lane's markers, the legend, and the
 * hover and details cards — handed to WorkTimeline.tsx as a few pieces it
 * places, so the timeline itself changes by a handful of lines.
 */
import { type PointerEvent, type ReactNode, type RefObject, useMemo, useState } from 'react';

import { CommitDetails, CommitLaneGlyph, CommitLegend, CommitMarkers, CommitTip } from './CommitOverlay.tsx';
import { clusterMarkers, commitLegend, commitsInWindow, commitTip, type CommitTipText, isCommitLane, type MarkerCluster, markerTone, overlayLanes, placeCommits, STAGE_SHAPE, type TimelineCommit } from './timelineCommitsModel.ts';
import { useTimelineOpen } from './timelineOpen.tsx';
import type { LanesBy, TimelineLane, TimeWindow } from './workTimelineModel.ts';

/** What a timeline is given to draw the overlay. */
export interface CommitOverlayInput {
  commits: readonly TimelineCommit[];
  /** A repository's default branch, for "In main". */
  mainBranch: (rootId: string) => string | undefined;
  /** A repository's remote's friendly name, for "Not on GitHub yet". */
  remoteLabel: (rootId: string) => string | undefined;
  repositoryName: (rootId: string) => string;
  /** What the legend calls the main branch (legendMainBranch). */
  legendMain?: string;
  /** The timeline is cut to one agent, team or plan: no lanes are added. */
  onlyRunLanes?: boolean;
  /** Console: the commit's page on GitHub (the history route's `url`). */
  commitUrl?: (c: TimelineCommit) => string | undefined;
}

export interface CommitOverlayContext {
  lanes: readonly TimelineLane[];
  lanesBy: LanesBy;
  win: TimeWindow;
  plotWidth: number;
  chartRef: RefObject<HTMLDivElement | null>;
  chartWidth: number;
  showRepository?: boolean;
}

export interface CommitOverlay {
  /** The run lanes (those with commits marked `commitBand`), then the overlay's own. */
  lanes: readonly TimelineLane[];
  /** Commits placed in a lane. */
  count: number;
  /** A lane's markers, inside its SVG, at `y` (workTimelineModel `commitMarkerY`). */
  markers: (lane: TimelineLane, y: number) => ReactNode;
  /** The glyph of a lane the overlay added; undefined for a run lane. */
  glyph: (lane: TimelineLane) => ReactNode | undefined;
  legend: ReactNode;
  /** The hover card and the details card, in the chart's box. */
  layer: ReactNode;
}

const TIP_MAX = 3;

/** The overlay for one timeline: nothing at all without `input`. */
export function useCommitOverlay(input: CommitOverlayInput | undefined, ctx: CommitOverlayContext): CommitOverlay {
  const { lanes, lanesBy, win, plotWidth, chartRef, chartWidth, showRepository } = ctx;
  const placed = useMemo(
    () => (input ? placeCommits(input.commits, lanes, lanesBy, { repositoryName: input.repositoryName, ...(showRepository ? { showRepository } : {}), ...(input.onlyRunLanes ? { onlyRunLanes: true } : {}) }) : undefined),
    [input, lanes, lanesBy, showRepository],
  );
  // A run lane with commits is one lane showing both: its markers go in a band under its bars, never on them.
  const allLanes = useMemo(() => (placed ? overlayLanes(lanes, placed) : lanes), [lanes, placed]);
  // The legend names the stages of the markers in view, as the runs' legend does the bars in view.
  const visible = useMemo(() => (placed ? commitsInWindow(placed.byLane, win) : []), [placed, win]);
  const [hover, setHover] = useState<{ cluster: MarkerCluster; x: number; y: number } | undefined>(undefined);
  const [open, setOpen] = useState<{ cluster: MarkerCluster; x: number; y: number } | undefined>(undefined);
  // Console: a commit opens on GitHub, its task on the project's board (./timelineOpen.tsx).
  const { openTimelineCommit, openCommitTask } = useTimelineOpen();

  if (!input || !placed) return { lanes, count: 0, markers: () => null, glyph: () => undefined, legend: null, layer: null };

  const at = (e: PointerEvent<SVGGElement>) => {
    const rect = chartRef.current?.getBoundingClientRect();
    return rect ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : { x: 0, y: 0 };
  };
  const tipOf = (c: TimelineCommit): CommitTipText => {
    const mainBranch = input.mainBranch(c.rootId);
    const remoteLabel = input.remoteLabel(c.rootId);
    return commitTip(c, { ...(mainBranch ? { mainBranch } : {}), ...(remoteLabel ? { remoteLabel } : {}), ...(showRepository ? { repositoryName: input.repositoryName(c.rootId) } : {}) });
  };

  const markers = (lane: TimelineLane, y: number) => {
    const list = placed.byLane.get(lane.key);
    if (!list?.length) return null;
    return (
      <CommitMarkers
        clusters={clusterMarkers(list, win, plotWidth)}
        y={y}
        mainBranch={input.mainBranch}
        hovered={hover?.cluster.key}
        onHover={(cluster, e) => setHover({ cluster, ...at(e) })}
        onLeave={() => setHover(undefined)}
        onOpen={(cluster) => {
          setHover(undefined);
          const rect = chartRef.current?.getBoundingClientRect();
          const el = chartRef.current?.querySelector(`[data-wtl-commit="${cluster.lead.hash}"]`)?.getBoundingClientRect();
          setOpen({ cluster, x: el && rect ? el.left - rect.left : 0, y: el && rect ? el.bottom - rect.top : 0 });
        }}
      />
    );
  };

  let layer: ReactNode = null;
  if (open) {
    const cardW = Math.min(336, chartWidth - 16);
    layer = (
      <CommitDetails
        key={open.cluster.key}
        cluster={open.cluster}
        tipOf={tipOf}
        left={Math.max(8, Math.min(chartWidth - cardW - 8, open.x - 12))}
        top={open.y + 6}
        onOpenCommit={(c) => openTimelineCommit(c, input.commitUrl?.(c))}
        onOpenTask={(c) => openCommitTask(c)}
        onClose={() => setOpen(undefined)}
      />
    );
  } else if (hover) {
    const newest = [...hover.cluster.commits].reverse();
    const tips = newest.slice(0, TIP_MAX).map((c) => ({ ...tipOf(c), shape: STAGE_SHAPE[c.stage], tone: markerTone(c) }));
    layer = <CommitTip tips={tips} more={newest.length - tips.length} left={Math.max(8, Math.min(chartWidth - 296, hover.x + 12))} top={hover.y + 14} />;
  }
  return {
    lanes: allLanes,
    count: placed.count,
    markers,
    glyph: (lane) => (isCommitLane(lane) ? <CommitLaneGlyph lane={lane} /> : undefined),
    legend: <CommitLegend items={commitLegend(visible, input.legendMain)} />,
    layer,
  };
}
